import React, { FC, useCallback, useState } from 'react';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { MATERIAL_ICON_CART } from '../../shared/common/Button/Icon';
import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import { PriceTier } from '../../../models/PriceTier';
import { BulkAddProductsToSelectionListResponse, BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { SelectionList } from '../../../models/SelectionList';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { useTranslation } from 'react-i18next';
import { AddToCartOptions, AddToListOptions } from '../../../models/ListContentCheckOptions';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { ConfirmOptionsModal, DEFAULT_SUBSCRIPTION_PERIOD_TYPE } from '../../shared/common/ConfirmOptionsModal';
import { SubscriptionPeriodType } from '../../../models/Article';
import { ACTION_ADD } from '../../store/constants';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';
import { useCreateNewSelectionList } from '../../api/selection-list/useCreateNewSelectionList';

type Props = {
    readonly onBulkAddToCart: (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onBulkAddToList: (
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onlyEpressProducts?: boolean;
    readonly defaultPriceTier: PriceTier;
    readonly isNothingSelected: boolean;
};

export const CatalogueMenuActionButtons: FC<Props> = ({
    onBulkAddToCart,
    onBulkAddToList,
    onlyEpressProducts = false,
    defaultPriceTier,
    isNothingSelected
}) => {
    const { selectionLists } = useSelectionLists();
    const [addOptions, setAddOptions] = useState<AddToListOptions | AddToCartOptions | undefined>(undefined);

    const onBulkAdd = useCallback(
        (
            performChecks: boolean,
            options: AddToListOptions | AddToCartOptions
        ): Promise<BulkAddProductsToSelectionListResponse | BulkAddProductsToShoppingCartResponse> => {
            if (options.target === ListActionTarget.List) {
                return onBulkAddToList(options.list, options.priceTier, options.subscriptionPeriodType, performChecks);
            } else {
                return onBulkAddToCart(options.priceTier, options.subscriptionPeriodType, performChecks);
            }
        },
        [onBulkAddToList, onBulkAddToCart]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } = useListContentChecks<
        AddToListOptions | AddToCartOptions
    >({ onAction: onBulkAdd });

    const { mutate: createSelectionList } = useCreateNewSelectionList();

    const { t } = useTranslation();

    const handleBulkAddToList = (list: SelectionList) => {
        setAddOptions({
            target: ListActionTarget.List,
            list,
            priceTier: defaultPriceTier,
            subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
            action: ACTION_ADD
        });
    };

    const handleBulkAddToCart = () => {
        setAddOptions({
            target: ListActionTarget.Cart,
            priceTier: defaultPriceTier,
            subscriptionPeriodType: DEFAULT_SUBSCRIPTION_PERIOD_TYPE,
            action: ACTION_ADD
        });
    };

    return (
        <>
            <Button
                text={t('addToCart')}
                icon={MATERIAL_ICON_CART}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NEUTRAL}
                onClick={handleBulkAddToCart}
                disabled={isNothingSelected}
            />
            <SelectionListDropDown
                label={t('addToList')}
                lists={selectionLists}
                onCreateNewListClicked={createSelectionList}
                onListClick={handleBulkAddToList}
                disabled={isNothingSelected}
            />

            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
            <ConfirmOptionsModal
                options={addOptions}
                changePrice={changePrice}
                showUsageConditionOption={true}
                showSubscriptionStartDateOption={onlyEpressProducts}
                closeModal={() => setAddOptions(undefined)}
            />
        </>
    );
};
